import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { cn } from "@/lib/utils";
import { PurchaseOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { freshdeskContactUrl } from "@/lib/freshdeskAgentUrl";
import { HubContactTokenInput } from "@/components/ui/HubContactTokenInput";
import {
  FreshdeskCompanyAutocomplete,
  type FreshdeskCompanySelection,
} from "@/components/ui/FreshdeskCompanyAutocomplete";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import type { HubContactSelection } from "@/lib/hubContactsApi";
import { SupplierArrivalFollowersSection } from "@/components/mrp/SupplierArrivalFollowersSection";
import {
  fetchSupplierCompanyContacts,
  fetchSupplierDetail,
  isFieldEditable,
  isFieldReadOnly,
  patchSupplier,
  type FieldOwnershipMap,
  type SupplierPurchaseOrderRow,
  type SupplierQuoteContactRow,
} from "@/lib/masterData";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { apiFetch } from "@/lib/api";
import {
  FALLBACK_MEGAVENTORY_PAYMENT_TERMS_LABELS,
  megaventoryEnumerationFromPaymentTermsUserInput,
} from "@/lib/megaventory-payment-terms-helpers";

const PAYMENT_METHOD_OPTIONS = [
  { value: "None", label: "None" },
  { value: "BankTransfer", label: "Bank transfer" },
  { value: "Credit", label: "Credit" },
  { value: "Cash", label: "Cash" },
  { value: "Other", label: "Other" },
] as const;

const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD" },
  { value: "CNY", label: "CNY" },
] as const;

const selectClassName =
  "h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-700";

const EDITABLE_PRIORITY = [
  "mv_supplier_name",
  "currency",
  "payment_terms",
  "payment_method",
  "mv_supplier_phone",
  "mv_supplier_email",
  "bill_to_address_line_1",
  "bill_to_city",
  "bill_to_state",
  "bill_to_zip",
] as const;

type SupplierDetailTab = "details" | "purchase-orders" | "contacts";

const SUPPLIER_DETAIL_TABS: { key: SupplierDetailTab; label: string }[] = [
  { key: "details", label: "Details" },
  { key: "purchase-orders", label: "Purchase orders" },
  { key: "contacts", label: "Contacts" },
];

function FieldRow({
  field,
  label,
  value,
  ownership,
  draft,
  onChange,
}: {
  field: string;
  label: string;
  value: string;
  ownership: FieldOwnershipMap;
  draft: Record<string, string>;
  onChange: (field: string, value: string) => void;
}) {
  const readOnly = isFieldReadOnly(ownership, field) || !isFieldEditable(ownership, field);
  const display = draft[field] ?? value;
  return (
    <div className="grid gap-1">
      <Label>{label}</Label>
      <Input
        value={display}
        disabled={readOnly}
        onChange={(event) => onChange(field, event.target.value)}
      />
    </div>
  );
}

function quoteContactsToSelections(rows: SupplierQuoteContactRow[]): HubContactSelection[] {
  return rows.map((row) => ({
    hubContactId: row.hub_contact_id,
    email: row.email,
    displayName: row.display_name,
    source: "hub",
    missing: false,
  }));
}

function selectionsToQuoteContactHubIds(contacts: HubContactSelection[]): number[] {
  return contacts.map((contact) => contact.hubContactId);
}

function quoteContactsDirty(
  saved: SupplierQuoteContactRow[],
  draft: HubContactSelection[] | null,
): boolean {
  if (draft == null) {
    return false;
  }
  const savedIds = saved.map((row) => row.hub_contact_id);
  const draftIds = selectionsToQuoteContactHubIds(draft);
  if (savedIds.length !== draftIds.length) {
    return true;
  }
  return savedIds.some((id, index) => id !== draftIds[index]);
}

export function SupplierLegacyDetailRedirect(): JSX.Element {
  const { id: idParam = "" } = useParams();
  const id = Number.parseInt(idParam, 10);
  if (!Number.isFinite(id)) {
    return <Navigate to="/mrp/suppliers" replace />;
  }
  return <Navigate to={`/mrp/suppliers?supplier=${String(id)}`} replace />;
}

export function SupplierDetailPanel({ supplierId }: { supplierId: number }): JSX.Element {
  const id = supplierId;
  const queryClient = useQueryClient();
  const [detailTab, setDetailTab] = useState<SupplierDetailTab>("details");
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [quoteContactsDraft, setQuoteContactsDraft] = useState<HubContactSelection[] | null>(null);
  const [freshdeskCompanyDraft, setFreshdeskCompanyDraft] = useState<FreshdeskCompanySelection | null | undefined>(
    undefined,
  );
  const canManageArrivalFollowers = useHubModuleAdmin("mrp");
  const { data, isLoading, error } = useQuery({
    queryKey: ["mrp-supplier", id],
    queryFn: () => fetchSupplierDetail(id),
    enabled: Number.isFinite(id),
  });
  const staffUsersQuery = useQuery({
    queryKey: ["notifications", "staff-user-options"],
    queryFn: () => apiFetch<{ users: { userId: string; email: string; displayName: string | null }[] }>(
      "/api/notifications/staff-user-options",
    ),
    enabled: canManageArrivalFollowers && Number.isFinite(id),
  });

  const supplier = data?.supplier ?? {};
  const ownership = data?.field_ownership ?? {};
  const savedQuoteContacts = data?.quote_contacts ?? [];
  const savedFreshdeskCompany = data?.freshdesk_company ?? null;
  const freshdeskCompany =
    freshdeskCompanyDraft === undefined
      ? savedFreshdeskCompany != null
        ? {
            id: savedFreshdeskCompany.freshdesk_company_id,
            name: savedFreshdeskCompany.freshdesk_company_name,
          }
        : null
      : freshdeskCompanyDraft;
  const quoteContacts = quoteContactsDraft ?? quoteContactsToSelections(savedQuoteContacts);
  const sourcingChannel =
    draft.sourcing_channel ??
    data?.sourcing_channel ??
    "catalog_web";
  const hubSupplyChainNotes =
    draft.hub_supply_chain_notes ??
    String(supplier.hub_supply_chain_notes ?? "");

  const saveMutation = useMutation({
    mutationFn: () => {
      const patch: Record<string, unknown> = { ...draft };
      delete patch.currency;
      if (draft.sourcing_channel != null) {
        patch.sourcing_channel = draft.sourcing_channel;
      }
      if (draft.hub_supply_chain_notes != null) {
        const trimmed = draft.hub_supply_chain_notes.trim();
        patch.hub_supply_chain_notes = trimmed === "" ? null : trimmed;
      }
      if (quoteContactsDraft != null) {
        patch.quote_contact_hub_ids = selectionsToQuoteContactHubIds(quoteContactsDraft);
      }
      if (freshdeskCompanyDraft !== undefined) {
        patch.freshdesk_company_id = freshdeskCompanyDraft?.id ?? null;
      }
      return patchSupplier(id, patch);
    },
    onSuccess: async () => {
      setDraft({});
      setQuoteContactsDraft(null);
      setFreshdeskCompanyDraft(undefined);
      await queryClient.invalidateQueries({ queryKey: ["mrp-supplier", id] });
    },
  });

  const freshdeskCompanyDirty =
    freshdeskCompanyDraft !== undefined &&
    (freshdeskCompanyDraft?.id ?? null) !== (savedFreshdeskCompany?.freshdesk_company_id ?? null);

  const hasUnsavedChanges =
    Object.keys(draft).length > 0 ||
    quoteContactsDirty(savedQuoteContacts, quoteContactsDraft) ||
    freshdeskCompanyDirty;

  const labels = useMemo(
    () =>
      Object.fromEntries(
        EDITABLE_PRIORITY.map((field) => [
          field,
          field.replace(/^mv_/, "").replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()),
        ]),
      ),
    [],
  );

  const paymentTermLabelsQuery = useQuery({
    queryKey: ["mrp", "megaventory-payment-term-labels"],
    queryFn: () =>
      apiFetch<{ rows: { sb_enumeration: string; display_label: string }[] }>(
        "/api/mrp/megaventory-payment-term-labels",
      ),
  });
  const enumerationToLabel = useMemo(() => {
    const rows = paymentTermLabelsQuery.data?.rows;
    if (rows == null || rows.length === 0) {
      return FALLBACK_MEGAVENTORY_PAYMENT_TERMS_LABELS;
    }
    const map: Record<string, string> = {};
    for (const row of rows) {
      map[row.sb_enumeration] = row.display_label;
    }
    return map;
  }, [paymentTermLabelsQuery.data?.rows]);

  const paymentTermsValue = megaventoryEnumerationFromPaymentTermsUserInput(
    draft.payment_terms ?? String(supplier.payment_terms ?? ""),
    enumerationToLabel,
  );
  const paymentTermsOptions = Object.entries(enumerationToLabel)
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: "base" }));
  if (!paymentTermsOptions.some((option) => option.value === paymentTermsValue)) {
    paymentTermsOptions.unshift({ value: paymentTermsValue, label: paymentTermsValue });
  }
  const paymentMethodValue = draft.payment_method ?? String(supplier.payment_method ?? "None");
  const paymentMethodOptions = [...PAYMENT_METHOD_OPTIONS] as { value: string; label: string }[];
  if (!paymentMethodOptions.some((option) => option.value === paymentMethodValue)) {
    paymentMethodOptions.unshift({ value: paymentMethodValue, label: paymentMethodValue });
  }
  const currencyValue = String(supplier.currency ?? "USD");
  const currencyOptions = [...CURRENCY_OPTIONS] as { value: string; label: string }[];
  if (!currencyOptions.some((option) => option.value === currencyValue)) {
    currencyOptions.unshift({ value: currencyValue, label: currencyValue });
  }

  const companyContactsQuery = useQuery({
    queryKey: ["mrp-supplier-company-contacts", id, savedFreshdeskCompany?.freshdesk_company_id ?? null],
    queryFn: () => fetchSupplierCompanyContacts(id),
    enabled: Number.isFinite(id) && detailTab === "contacts" && savedFreshdeskCompany != null,
  });
  const purchaseOrders = data?.purchase_orders ?? [];
  const purchaseOrderColumns = useMemo((): HubDataTableColumn<SupplierPurchaseOrderRow>[] => {
    return [
      {
        id: "po_number",
        header: "Purchase order",
        cell: (row) => (
          <PurchaseOrderDetailLink
            purchaseOrderId={row.id}
            purchaseOrderNumber={row.po_number}
          />
        ),
      },
      {
        id: "hub_status",
        header: "Status",
        cell: (row) => row.hub_status.replace(/^\w/, (character) => character.toUpperCase()),
      },
      {
        id: "created_at",
        header: "Created",
        cell: (row) => (row.created_at != null ? new Date(row.created_at).toLocaleDateString() : "—"),
      },
    ];
  }, []);

  if (!Number.isFinite(id)) {
    return <p className="text-sm text-red-600">Invalid supplier id.</p>;
  }

  if (data == null && isLoading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="h-8 w-48 animate-pulse rounded bg-slate-200" />
        <Card>
          <CardHeader>
            <CardTitle>Fields</CardTitle>
          </CardHeader>
          <CardBody className="grid gap-3 md:grid-cols-2">
            {EDITABLE_PRIORITY.map((field) => (
              <div key={field} className="grid gap-2">
                <div className="h-4 w-28 animate-pulse rounded bg-slate-200" />
                <div className="h-9 animate-pulse rounded-md bg-slate-100" />
              </div>
            ))}
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <Card className="flex min-h-0 flex-col">
      <CardHeader className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>{String(supplier.mv_supplier_name ?? "Supplier")}</CardTitle>
        <HubSaveButton
          type="button"
          dirty={hasUnsavedChanges}
          isLoading={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          Save
        </HubSaveButton>
      </CardHeader>
      <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 px-4">
        <div className="flex min-w-0 flex-wrap gap-1" role="tablist" aria-label="Supplier">
          {SUPPLIER_DETAIL_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={detailTab === tab.key}
              className={cn(
                "-mb-px border-b-2 px-3 py-2 text-sm font-medium",
                detailTab === tab.key
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-600 hover:text-slate-900",
              )}
              onClick={() => setDetailTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <CardBody className={detailTab === "details" || detailTab === "contacts" ? "min-h-0 flex-1 space-y-4 overflow-y-auto" : "flex min-h-0 flex-1 flex-col overflow-hidden p-0"}>
      {detailTab === "details" ? (
      <div className="space-y-4">
      {supplier.megaventory_deleted_at != null ? (
        <p className="text-sm text-slate-700">
          Deleted in Megaventory.
          {data?.merged_into != null ? (
            <>
              {" "}
              Merged into{" "}
              <Link className="text-blue-700 hover:underline" to={`/mrp/suppliers/${data.merged_into.id}`}>
                {data.merged_into.mv_supplier_name} (MV {data.merged_into.mv_supplier_id})
              </Link>
              .
            </>
          ) : (
            " No surviving supplier is recorded."
          )}
        </p>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2">
          {error ? <p className="text-sm text-red-600 md:col-span-2">{(error as Error).message}</p> : null}
          {EDITABLE_PRIORITY.map((field) => {
            if (field === "payment_terms" || field === "payment_method" || field === "currency") {
              const readOnly =
                field === "currency" ||
                isFieldReadOnly(ownership, field) ||
                !isFieldEditable(ownership, field);
              const options =
                field === "payment_terms"
                  ? paymentTermsOptions
                  : field === "payment_method"
                    ? paymentMethodOptions
                    : currencyOptions;
              const value =
                field === "payment_terms"
                  ? paymentTermsValue
                  : field === "payment_method"
                    ? paymentMethodValue
                    : currencyValue;
              const control = (
                <select
                  className={selectClassName}
                  value={value}
                  disabled={readOnly}
                  onChange={(event) => {
                    if (field === "currency") return;
                    setDraft((prev) => ({ ...prev, [field]: event.target.value }));
                  }}
                >
                  {options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              );
              return (
                <div key={field} className="grid gap-1">
                  <Label>{labels[field] ?? field}</Label>
                  {field === "currency" ? (
                    <ReadOnlyHoverLayer active hoverHint="Currency is maintained in Megaventory." layout="block">
                      {control}
                    </ReadOnlyHoverLayer>
                  ) : (
                    control
                  )}
                </div>
              );
            }
            return (
              <FieldRow
                key={field}
                field={field}
                label={labels[field] ?? field}
                value={String(supplier[field] ?? "")}
                ownership={ownership}
                draft={draft}
                onChange={(key, value) => setDraft((prev) => ({ ...prev, [key]: value }))}
              />
            );
          })}
          <div className="grid gap-1 md:col-span-2">
            <Label>Sourcing channel</Label>
            <select
              className={selectClassName}
              value={sourcingChannel}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, sourcing_channel: event.target.value }))
              }
            >
              <option value="catalog_web">Catalog webstore</option>
              <option value="contract_manufacturer">Contract manufacturer</option>
            </select>
          </div>
          <div className="grid gap-1 md:col-span-2">
            <Label htmlFor="hub-supply-chain-notes">Hub supply chain notes</Label>
            <TextArea
              id="hub-supply-chain-notes"
              rows={6}
              value={hubSupplyChainNotes}
              disabled={isLoading}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, hub_supply_chain_notes: event.target.value }))
              }
            />
            <p className="text-xs text-slate-500">
              Hub-controlled notes stored only in Supabase (not mirrored to Megaventory).
            </p>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="supplier-freshdesk-company">Freshdesk company</Label>
          <FreshdeskCompanyAutocomplete
            id="supplier-freshdesk-company"
            company={freshdeskCompany}
            onCompanyChange={setFreshdeskCompanyDraft}
            disabled={saveMutation.isPending}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="supplier-quote-contacts">Contacts for quotes</Label>
          <HubContactTokenInput
            id="supplier-quote-contacts"
            recipients={quoteContacts}
            onRecipientsChange={setQuoteContactsDraft}
            freshdeskCompanyId={freshdeskCompany?.id ?? null}
            allowUnscopedSearch={freshdeskCompany == null}
            disabled={saveMutation.isPending}
            placeholder="Search contacts for RFQ emails…"
          />
        </div>
        <SupplierArrivalFollowersSection
          supplierId={id}
          canManageFollowers={canManageArrivalFollowers}
          userOptions={staffUsersQuery.data?.users ?? []}
          usersLoading={staffUsersQuery.isLoading}
        />
      </div>
      ) : null}
      {detailTab === "purchase-orders" ? (
        <HubDataTable
          className="min-h-0 flex-1 rounded-none border-0 shadow-none"
          columns={purchaseOrderColumns}
          rows={purchaseOrders}
          rowKey={(row) => row.id}
          loading={isLoading && purchaseOrders.length === 0}
          loadingMessage="Loading purchase orders…"
          error={error != null && purchaseOrders.length === 0 ? (error as Error).message : null}
          empty="No purchase orders for this supplier."
        />
      ) : null}
      {detailTab === "contacts" ? (
        savedFreshdeskCompany == null ? (
          <p className="text-sm text-slate-600">Link a Freshdesk company on Details to see its contacts.</p>
        ) : (
          <HubDataTable
            className="min-h-0 flex-1 rounded-none border-0 shadow-none"
            columns={[
              {
                id: "name",
                header: "Name",
                cell: (row) => (
                  <HoverHintTrigger
                    trigger={
                      <a
                        className="font-medium text-sky-700 hover:text-sky-900 hover:underline"
                        href={freshdeskContactUrl(row.id)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {row.name}
                      </a>
                    }
                  >
                    <HoverHintDefinitionGrid
                      rows={[
                        { label: "Name", value: row.name },
                        { label: "Email", value: row.email ?? "—" },
                        { label: "Title", value: row.job_title ?? "—" },
                      ]}
                    />
                  </HoverHintTrigger>
                ),
              },
              { id: "email", header: "Email", cell: (row) => row.email ?? "—" },
              { id: "job_title", header: "Title", cell: (row) => row.job_title ?? "—" },
            ]}
            rows={companyContactsQuery.data?.rows ?? []}
            rowKey={(row) => row.id}
            loading={companyContactsQuery.isLoading}
            loadingMessage="Loading contacts…"
            error={companyContactsQuery.data?.load_error ?? (companyContactsQuery.isError ? (companyContactsQuery.error as Error).message : null)}
            empty="No Freshdesk contacts for this company."
          />
        )
      ) : null}
      </CardBody>
    </Card>
  );
}
